import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import { userAPI } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Sécurité : extraire uniquement les valeurs primitives
  const userEmail = user?.email || 'Non disponible';
  const userRole = user?.role || 'MUTUALISTE';
  const userStatus = user?.status?.libelle || 'Actif(ve)';
  const userCreatedAt = user?.createdAt;
  const userLastLogin = user?.lastLogin;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    defaultValues: {}
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword
  } = useForm();

  const onSubmitProfile = async (data) => {
    setIsLoading(true);
    try {
      const response = await userAPI.updateUser(user.id, data);
      updateUser(response.data.user);
      toast.success('Profil mis à jour avec succès');
      setIsEditing(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitPassword = async (data) => {
    if (data.newPassword !== data.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setIsLoading(true);
    try {
      await userAPI.changePassword(user.id, {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });
      toast.success('Mot de passe modifié avec succès');
      setShowPasswordForm(false);
      resetPassword();
    } catch (error) {
      toast.error('Erreur lors de la modification du mot de passe');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset({});
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <h1 className="text-2xl font-bold text-primary-900 mb-2">
          Mon profil
        </h1>
        <p className="text-gray-600">
          Gérez vos informations personnelles et paramètres de compte
        </p>
      </div>

      {/* Toutes les sections sur une seule ligne */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations personnelles */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <h2 className="text-xl font-semibold text-primary-900 mb-6">
            Informations personnelles
          </h2>
          
          <div className="space-y-4">
            {/* Email (non modifiable) */}
            <div>
              <label className="form-label">Email</label>
              <p className="text-gray-900">{userEmail}</p>
              <p className="mt-1 text-xs text-gray-500">
                L'email ne peut pas être modifié
              </p>
            </div>

            {/* Rôle (non modifiable) */}
            <div>
              <label className="form-label">Rôle</label>
              <p className="text-gray-900">
                {userRole === 'ADMIN' ? 'Administrateur' : 'Mutualiste'}
              </p>
            </div>

            {/* Statut (non modifiable) */}
            <div>
              <label className="form-label">Statut</label>
              <div className="flex items-center space-x-2">
                <span className={`badge badge-${userStatus === 'Actif(ve)' ? 'success' : 'warning'}`}>
                  {userStatus}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Sécurité */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-primary-900">
              Sécurité
            </h2>
            {!showPasswordForm && (
              <button
                onClick={() => setShowPasswordForm(true)}
                className="btn-secondary btn-sm"
              >
                Changer le mot de passe
              </button>
            )}
          </div>

          {showPasswordForm ? (
            <form onSubmit={handlePasswordSubmit(onSubmitPassword)} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="form-label">Mot de passe actuel</label>
                  <input
                    type="password"
                    className={`form-input ${passwordErrors.currentPassword ? 'border-red-300' : ''}`}
                    {...registerPassword('currentPassword', {
                      required: 'Le mot de passe actuel est requis'
                    })}
                  />
                  {passwordErrors.currentPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.currentPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Nouveau mot de passe</label>
                  <input
                    type="password"
                    className={`form-input ${passwordErrors.newPassword ? 'border-red-300' : ''}`}
                    {...registerPassword('newPassword', {
                      required: 'Le nouveau mot de passe est requis',
                      minLength: { value: 6, message: 'Le mot de passe doit contenir au moins 6 caractères' }
                    })}
                  />
                  {passwordErrors.newPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.newPassword.message}</p>
                  )}
                </div>

                <div>
                  <label className="form-label">Confirmer le mot de passe</label>
                  <input
                    type="password"
                    className={`form-input ${passwordErrors.confirmPassword ? 'border-red-300' : ''}`}
                    {...registerPassword('confirmPassword', {
                      required: 'La confirmation du mot de passe est requise'
                    })}
                  />
                  {passwordErrors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{passwordErrors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <LoadingSpinner size="small" className="mr-2" />
                      Modification...
                    </div>
                  ) : (
                    'Modifier le mot de passe'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    resetPassword();
                  }}
                  className="btn-secondary"
                >
                  Annuler
                </button>
              </div>
            </form>
          ) : (
            <div className="text-gray-600">
              <p>Dernière modification du mot de passe : Jamais</p>
              <p className="text-sm mt-2">
                Pour votre sécurité, nous recommandons de changer votre mot de passe régulièrement.
              </p>
            </div>
          )}
        </div>

        {/* Informations du compte */}
        <div className="bg-white rounded-xl shadow-card p-6">
          <h2 className="text-xl font-semibold text-primary-900 mb-6">
            Informations du compte
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="form-label">Date d'inscription</label>
              <p className="text-gray-900">
                {userCreatedAt ? new Date(userCreatedAt).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'Non disponible'}
              </p>
            </div>
            
            <div>
              <label className="form-label">Dernière connexion</label>
              <p className="text-gray-900">
                {userLastLogin ? new Date(userLastLogin).toLocaleDateString('fr-FR', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Première connexion'}
              </p>
            </div>

            <div>
              <label className="form-label">Statut du compte</label>
              <p className="text-gray-900">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  userStatus === 'Actif(ve)' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {userStatus}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
